import { HexColor } from '@repo/shared/types';
import { TrackingEvent } from '@transcend-io/segment/core';
import { groq } from 'next-sanity';
import { z } from 'zod';

import { awardsCta, SanityAwardsCta } from './awardsCta';
import { basicText, SanityBasicText } from './basicText';
import { bentoBoxItem, SanityBentoBox } from './bentoBoxItem';
import { button, SanityButton } from './button';
import { cardButton, SanityCardButton } from './buttonRow';
import { cardGrid, SanityCardGrid } from './cardGrid';
import { comparisonCards, SanityComparisonCards } from './comparisonCards';
import {
  comparisonTable,
  SanityComparisonTable,
} from './comparisonTableSection';
import { eventHero, SanityEventHero } from './eventHero';
import {
  featureComparison,
  SanityFeatureComparison,
} from './featureComparison';
import { footnotes, SanityFootnotes } from './footnotes';
import {
  form,
  SanityForm,
  SanityFormTrackingEventProperties,
  SanityHubspotConvertingFormType,
} from './form';
import { formConfirmation, SanityFormConfirmation } from './formConfirmation';
import { fullWidthVideo, SanityFullWidthVideo } from './fullWidthVideo';
import { heroCentered, SanityHeroCentered } from './heroCentered';
import { heroForm, SanityHeroForm } from './heroForm';
import { heroHome, SanityHeroHome } from './heroHome';
import { heroSplit, SanityHeroSplit } from './heroSplit';
import {
  homepageContentCards,
  SanityHomepageContentCards,
} from './homepageContentCards';
import { homepageEvents, SanityHomepageEvents } from './homepageEvents';
import { image, SanityImage } from './image';
import { imageGrid, SanityImageGrid } from './imageGrid';
import { link, SanityLink } from './link';
import { linkCardFields, SanityLinkCard } from './linkCard';
import { logoMarquee, SanityLogoMarquee } from './logoMarquee';
import {
  media,
  mediaWithIcon,
  SanityMedia,
  SanityMediaWithIcon,
} from './media';
import {
  policyEngineAnimation,
  SanityPolicyEngineAnimation,
} from './policyEngineAnimation';
import { railsFormLanding, SanityRailsFormLanding } from './railsFormLanding';
import { relatedContent, SanityRelatedContent } from './relatedContent';
import { richAccordion, SanityRichAccordion } from './richAccordion';
import { richText } from './richText';
import { richTextSection, SanityRichTextSection } from './richTextSection';
import { SanitySombraStack, sombraStack } from './sombraStack';
import { SanityStats, stats } from './stats';
import { SanityTableSection, tableSection } from './tableSection';
import { SanityTestimonial, testimonial } from './testimonial';
import {
  SanityTestimonialCarousel,
  testimonialCarousel,
} from './testimonialCarousel';
import {
  SanityTestimonialSection,
  testimonialSection,
} from './testimonialSection';
import { SanityThreeUp, threeUp } from './threeUp';
import { SanityTwoUp, twoUp } from './twoUp';
import { SanityTwoUpForm, twoUpForm } from './twoUpForm';
import { SanityVideoEmbed, videoEmbed } from './videoEmbed';
import { SanityVideoTwoUp, videoTwoUp } from './videoTwoUp';

export const SanityHomePageTwoUp = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('homePageTwoUp'),
  _key: z.string(),
  overline: z.string().optional().nullable(),
  headline: z.string(),
  bodyCopy: z.string(),
  media: SanityImage,
  mobileMedia: SanityImage,
});
export type SanityHomePageTwoUp = z.infer<typeof SanityHomePageTwoUp>;

export const SanityHomePageHero = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('homePageHero'),
  _key: z.string(),
  headline: z.string(),
  bodyCopy: SanityBasicText.optional(),
  form: SanityForm.optional().nullable(),
  formConfirmation: z.string(),
  formSubtextLink: SanityLink.optional().nullable(),
  media: SanityImage,
  mobileMedia: SanityImage,
});
export type SanityHomePageHero = z.infer<typeof SanityHomePageHero>;

export const SanityHomePagePainpoint = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('homePagePainpoint'),
  _key: z.string(),
  eyebrow: z.string().optional().nullable(),
  headline: z.string().optional().nullable(),
  bodyCopy: SanityBasicText.optional().nullable(),
  media: SanityImage,
  mobileMedia: SanityImage,
});
export type SanityHomePagePainpoint = z.infer<typeof SanityHomePagePainpoint>;

export const SanityRailsArchitecture = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('railsArchitecture'),
  _key: z.string(),
  headline: z.string(),
});
export type SanityRailsArchitecture = z.infer<typeof SanityRailsArchitecture>;

export const SanityRailsSombraCombo = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('railsSombraCombo'),
  _key: z.string(),
  headline: z.string(),
});
export type SanityRailsSombraCombo = z.infer<typeof SanityRailsSombraCombo>;

export const SanityTwoUpScroller = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('twoUpScroller'),
  _key: z.string(),
  overline: z.string().optional().nullable(),
  headline: z.string().optional().nullable(),
  sections: z.array(
    z.object({
      _key: z.string(),
      overline: z.string().optional().nullable(),
      headline: z.string(),
      bodyCopy: z.string(),
      link: SanityLink.optional().nullable(),
      media: SanityMedia,
    }),
  ),
});
export type SanityTwoUpScroller = z.infer<typeof SanityTwoUpScroller>;

export const SanityTestimonialMarquee = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('testimonialMarquee'),
  _key: z.string(),
  testimonials: z.array(SanityTestimonial),
});
export type SanityTestimonialMarquee = z.infer<typeof SanityTestimonialMarquee>;

export const SanityCallToAction = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('callToAction'),
  _key: z.string(),
  theme: z.enum(['light', 'charcoal', 'purple']),
  media: SanityMediaWithIcon.optional().nullable(),
  headline: z.string(),
  bodyCopy: z.string().optional().nullable(),
  ctaMode: z.enum([
    'none',
    'button',
    'buttonRow',
    'standardForm',
    'redirectForm',
  ]),
  /** Legacy single-button CTA; used when ctaMode is `button`. */
  cta: SanityLink.optional().nullable(),
  buttons: z.array(SanityCardButton).optional().nullable(),
  form: SanityForm.optional().nullable(),
  formConfirmation: SanityFormConfirmation.optional().nullable(),
  formFunnel: z.enum(['contact', 'rails']).optional().nullable(),
  primaryButtonLabel: z.string().optional().nullable(),
  secondaryButton: SanityLink.optional().nullable(),
  legalText: SanityBasicText.optional().nullable(),
  background: HexColor.optional().nullable(),
  foreground: HexColor.optional().nullable(),
});
export type SanityCallToAction = z.infer<typeof SanityCallToAction>;

export const SanityHeroBasic = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('heroBasic'),
  _key: z.string(),
  overline: z.string().optional().nullable(),
  headline: z.string(),
  headlineSize: z.enum(['normal', 'large']),
  bodyCopy: z.string().optional().nullable(),
  button: SanityButton,
  background: HexColor.optional().nullable(),
  foreground: HexColor.optional().nullable(),
});
export type SanityHeroBasic = z.infer<typeof SanityHeroBasic>;

export const SanityHeroProduct = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('heroProduct'),
  _key: z.string(),
  productIcon: SanityImage,
  productTitle: z.string(),
  headline: z.string(),
  bodyCopy: z.string().optional().nullable(),
  button: SanityButton,
  media: SanityMedia.optional().nullable(),
});
export type SanityHeroProduct = z.infer<typeof SanityHeroProduct>;

export const SanityHeroTwoUp = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('heroTwoUp'),
  _key: z.string(),
  overline: z.string().optional().nullable(),
  headline: z.string(),
  bodyCopy: SanityBasicText.optional().nullable(),
  button: SanityButton,
  media: SanityMedia,
  background: HexColor.optional().nullable(),
  foreground: HexColor.optional().nullable(),
});
export type SanityHeroTwoUp = z.infer<typeof SanityHeroTwoUp>;

export const SanityHeroNewsletter = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('heroNewsletter'),
  _key: z.string(),
  headline: z.string(),
  bodyCopy: z.string().optional().nullable(),
  emailInputPlaceholder: z.string(),
  submitButtonLabel: z.string(),
  legalText: SanityBasicText.optional().nullable(),
  trackingEventName: TrackingEvent,
  trackingEventProperties: SanityFormTrackingEventProperties,
  hubspotCampaignId: z.string().uuid().nullish(),
  hubspotConvertingFormType: SanityHubspotConvertingFormType,
  hubspotEmailSubscriptionIds: z.array(z.string()).nullish(),
  confirmation: z.string(),
  media: SanityMedia,
  background: HexColor.optional().nullable(),
  foreground: HexColor.optional().nullable(),
});
export type SanityHeroNewsletter = z.infer<typeof SanityHeroNewsletter>;

export const SanityLinkCardGrid = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('linkCardGrid'),
  _key: z.string(),
  headline: z.string().optional().nullable(),
  cards: z.array(SanityLinkCard),
});
export type SanityLinkCardGrid = z.infer<typeof SanityLinkCardGrid>;

export const SanityFaqSection = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('faqSection'),
  _key: z.string(),
  questions: z.array(
    z.object({
      _key: z.string(),
      question: z.string(),
      answer: SanityBasicText,
    }),
  ),
});
export type SanityFaqSection = z.infer<typeof SanityFaqSection>;

export const SanityTwoUpCard = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('twoUpCard'),
  _key: z.string(),
  headline: z.string(),
  bodyCopy: z.string().optional().nullable(),
  formattedBodyCopy: SanityBasicText.optional().nullable(),
  cta: SanityLink.optional().nullable(),
  media: SanityMedia,
  hasMediaPadding: z.boolean(),
  contentBackground: HexColor,
  mediaBackground: HexColor.optional().nullable(),
  desktopLayout: z.enum(['imageText', 'textImage']),
  mobileLayout: z.enum(['imageText', 'textImage']),
});
export type SanityTwoUpCard = z.infer<typeof SanityTwoUpCard>;

export const SanityPhotoMarquee = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('photoMarquee'),
  _key: z.string(),
  photos: z.array(
    z.object({
      _key: z.string(),
      image: SanityImage,
      imageWidth: z.number(),
      caption: SanityBasicText.nullable(),
    }),
  ),
});
export type SanityPhotoMarquee = z.infer<typeof SanityPhotoMarquee>;

export const SanityEditorialCollage = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('editorialCollage'),
  _key: z.string(),
  overline: z.string().optional().nullable(),
  headline: z.string(),
  bodyCopy: SanityBasicText,
  cta: SanityLink.optional().nullable(),
  media: z.tuple([SanityMedia, SanityMedia]),
});
export type SanityEditorialCollage = z.infer<typeof SanityEditorialCollage>;

export const SanityPartnerGrid = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('partnerGrid'),
  _key: z.string(),
  headline: z.string(),
  bodyCopy: z.string().optional().nullable(),
  gridType: z.enum(['logos', 'people']),
  items: z.array(
    z.object({
      _key: z.string(),
      image: SanityImage,
      title: z.string(),
      caption: z.string().optional().nullable(),
    }),
  ),
});
export type SanityPartnerGrid = z.infer<typeof SanityPartnerGrid>;

export const SanityNewsletterListing = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('newsletterListing'),
  _key: z.string(),
  headline: z.string(),
  issues: z.array(
    z.object({
      _key: z.string(),
      subjectLine: z.string(),
      publishedAt: z.string(),
      url: z.string(),
      thumbnailImage: SanityImage,
    }),
  ),
});
export type SanityNewsletterListing = z.infer<typeof SanityNewsletterListing>;

export const SanityTimelineEvent = z.object({
  _type: z.literal('timelineEvent'),
  _key: z.string(),
  year: z.number().nonnegative(),
  companyHistory: SanityBasicText.optional().nullable(),
  industryContext: SanityBasicText.optional().nullable(),
  media: SanityMedia.optional().nullable(),
  mediaSize: z.enum(['small', 'medium', 'large']),
  mediaPosition: z.enum(['left', 'right']),
  mediaOrder: z.enum(['before', 'after']),
});
export type SanityTimelineEvent = z.infer<typeof SanityTimelineEvent>;

export const SanityTimelineTestimonial = SanityTestimonial.extend({
  _type: z.literal('timelineTestimonial'),
  _key: z.string(),
});
export type SanityTimelineTestimonial = z.infer<
  typeof SanityTimelineTestimonial
>;

export const SanityTimelineTextBox = z.object({
  _type: z.literal('timelineTextBox'),
  _key: z.string(),
  headline: z.string(),
  content: SanityBasicText,
});
export type SanityTimelineTextBox = z.infer<typeof SanityTimelineTextBox>;

export const SanityTimeline = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('timeline'),
  _key: z.string(),
  sections: z.array(
    z.discriminatedUnion('_type', [
      SanityTimelineEvent,
      SanityTimelineTestimonial,
      SanityVideoEmbed,
      SanityTimelineTextBox,
    ]),
  ),
});
export type SanityTimeline = z.infer<typeof SanityTimeline>;

export const SanityCareersFeed = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('careersFeed'),
  _key: z.string(),
  title: z.string(),
});
export type SanityCareersFeed = z.infer<typeof SanityCareersFeed>;

export const SanityTestimonialGrid = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('testimonialGrid'),
  _key: z.string(),
  items: z.array(
    z.discriminatedUnion('_type', [
      SanityTestimonial.extend({
        _key: z.string(),
        _type: z.literal('testimonialCard'),
        logoWidth: z.number().optional().nullable(),
      }),
      z.object({
        _key: z.string(),
        _type: z.literal('inlineCallToAction'),
        headline: z.string(),
        link: SanityLink,
      }),
    ]),
  ),
});
export type SanityTestimonialGrid = z.infer<typeof SanityTestimonialGrid>;

export const SanityLogoGrid = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('logoGrid'),
  _key: z.string(),
  headline: z.string(),
  background: z.literal('#F4F4F5'),
  items: z.array(
    z.object({
      _key: z.string(),
      logo: SanityImage,
      logoWidth: z.number(),
      link: SanityLink,
    }),
  ),
});
export type SanityLogoGrid = z.infer<typeof SanityLogoGrid>;

export const SanityPressListing = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('pressListing'),
  _key: z.string(),
  headline: z.string(),
  items: z.array(
    z.object({
      _key: z.string(),
      title: z.string(),
      publishedAt: z.string(),
      source: z.string(),
      url: z.string(),
      thumbnailImage: SanityImage,
    }),
  ),
});
export type SanityPressListing = z.infer<typeof SanityPressListing>;

export const SanityDocumentTemplate = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('documentTemplate'),
  _key: z.string(),
  pageTitle: z.string(),
  toc: z.object({
    excludedStrings: z.array(z.string()),
    legalText: SanityBasicText,
  }),
  content: SanityBasicText,
});
export type SanityDocumentTemplate = z.infer<typeof SanityDocumentTemplate>;

export const SanityAnchorPoint = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('anchorPoint'),
  _key: z.string(),
  id: z.string(),
});
export type SanityAnchorPoint = z.infer<typeof SanityAnchorPoint>;

export const SanityDivider = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('divider'),
  _key: z.string(),
  theme: z.enum(['white', 'gray', 'dark', 'cipher']),
});
export type SanityDivider = z.infer<typeof SanityDivider>;

export const SanitySolutionsTabsColorThemes = z.enum([
  'flame',
  'gold',
  'sky',
  'lush',
  'amethyst',
  'apple',
]);
export type SanitySolutionsTabsColorThemes = z.infer<
  typeof SanitySolutionsTabsColorThemes
>;

export const SanitySolutionsTabs = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('solutionsTabs'),
  _key: z.string(),
  headline: z.string().optional().nullable(),
  renderGrayscale: z.boolean().optional().nullable(),
  tabs: z.array(
    z.object({
      _key: z.string(),
      label: z.string(),
      headline: z.string(),
      eyebrow: z.string().optional().nullable(),
      content: SanityBasicText,
      link: SanityLink.optional().nullable(),
      // Optional when renderGrayscale hides the field in Studio
      colorTheme: SanitySolutionsTabsColorThemes.optional().nullable(),
      media: SanityMedia,
      mobileMedia: SanityMedia.optional().nullable(),
      hasMediaPadding: z.boolean(),
    }),
  ),
});
export type SanitySolutionsTabs = z.infer<typeof SanitySolutionsTabs>;

export const SanityLayoutOptionsSchema = z.enum(['imageText', 'textImage']);
export type SanityLayoutOptionsSchema = z.infer<
  typeof SanityLayoutOptionsSchema
>;

export const SanityCalculatorSection = z.object({
  _type: z.literal('calculator'),
  _key: z.string(),
  sections: z.array(
    z.union([
      z.object({
        _type: z.literal('sectionHeader'),
        _key: z.string(),
        title: z.string(),
        description: SanityBasicText.optional().nullable(),
      }),
      z.object({
        _type: z.literal('field'),
        _key: z.string(),
        variable: z.string(),
        label: z.string(),
        defaultValue: z.number().optional().nullable(),
      }),
      z.object({
        _type: z.literal('results'),
        _key: z.string(),
        title: z.string(),
        results: z.array(
          z.object({
            _key: z.string(),
            label: z.string(),
            calculation: z.string(),
            format: z.string().optional().nullable(),
          }),
        ),
        background: z.string().optional().nullable(),
      }),
      z.object({
        _type: z.literal('calculatorNote'),
        _key: z.string(),
        icon: SanityImage.optional().nullable(),
        text: z.string(),
        background: z.string().optional().nullable(),
      }),
    ]),
  ),
  calculations: z.array(
    z.object({
      _key: z.string(),
      variable: z.string(),
      label: z.string(),
      equation: z.string(),
    }),
  ),
});
export type SanityCalculatorSection = z.infer<typeof SanityCalculatorSection>;

const SanityPageSectionNonCircular = z.discriminatedUnion('_type', [
  SanityLogoMarquee,
  SanityTestimonialSection,
  SanityTwoUp,
  SanityVideoTwoUp,
  SanityFullWidthVideo,
  SanityTwoUpScroller,
  SanityTestimonialMarquee,
  SanityCallToAction,
  SanityHeroBasic,
  SanityHeroProduct,
  SanityHeroTwoUp,
  SanityHeroNewsletter,
  SanityThreeUp,
  SanityStats,
  SanityLinkCardGrid,
  SanityFaqSection,
  SanityTwoUpCard,
  SanityPhotoMarquee,
  SanityEditorialCollage,
  SanityPartnerGrid,
  SanityNewsletterListing,
  SanityBentoBox,
  SanityCareersFeed,
  SanityTestimonialCarousel,
  SanityTestimonialGrid,
  SanityLogoGrid,
  SanityPressListing,
  SanityRichAccordion,
  SanityRelatedContent,
  SanityHomepageContentCards,
  SanityHomepageEvents,
  SanityRichTextSection,
  SanityTwoUpForm,
  SanityDocumentTemplate,
  SanityAnchorPoint,
  SanityDivider,
  SanitySolutionsTabs,
  SanityTimeline,
  SanityComparisonTable,
  SanityFeatureComparison,
  SanityComparisonCards,
  SanityHeroForm,
  SanityEventHero,
  SanityHomePageHero,
  SanityHomePageTwoUp,
  SanityHomePagePainpoint,
  SanityRailsArchitecture,
  SanityRailsSombraCombo,
  SanityAwardsCta,
  SanityCardGrid,
  SanityImageGrid,
  SanityHeroCentered,
  SanityHeroHome,
  SanityHeroSplit,
  SanitySombraStack,
  SanityPolicyEngineAnimation,
  SanityTableSection,
  SanityRailsFormLanding,
  SanityFootnotes,
]);
type SanityPageSectionNonCircular = z.infer<
  typeof SanityPageSectionNonCircular
>;

export const SanityPageSection: z.ZodType<
  SanityPageSectionNonCircular | SanitySectionReference
> = z.lazy(() =>
  z.discriminatedUnion('_type', [
    SanitySectionReference,
    ...SanityPageSectionNonCircular.options,
  ]),
);
export type SanityPageSection = z.infer<typeof SanityPageSection>;

export const SanitySectionReference = z.object({
  sectionIndex: z.number().optional().nullable(),
  _type: z.literal('sectionReference'),
  _key: z.string(),
  referencedSectionId: z.string().optional().nullable(),
  sections: z.array(SanityPageSection),
});
// This is explicitly typed to avoid circular references
type SanitySectionReference = {
  sectionIndex?: number | null;
  _type: 'sectionReference';
  _key: string;
  referencedSectionId?: string | null;
  sections: SanityPageSection[];
};

export const sections = groq`{
  _type,
  _key,
  _type == 'logoMarquee' => ${logoMarquee},
  _type == 'testimonialSection' => ${testimonialSection},
  _type == 'homePageTwoUp' => {
    overline,
    headline,
    bodyCopy,
   media ${image},
    mobileMedia ${image},
  },
  _type == 'homePagePainpoint' => {
    headline,
    bodyCopy[] ${basicText},
    media ${image},
    mobileMedia ${image},
    eyebrow,
  },
  _type == 'railsArchitecture' => {
    headline,
  },
  _type == 'railsSombraCombo' => {
    headline,
  },
  _type == 'twoUp' => ${twoUp},
  _type == 'videoTwoUp' => ${videoTwoUp},
  _type == 'fullWidthVideo' => ${fullWidthVideo},
  _type == 'twoUpScroller' => {
    overline,
    headline,
    sections[] {
      _key,
      overline,
      headline,
      bodyCopy,
      link[0] ${link},
      media[0] ${media},
    },
  },
  _type == 'testimonialMarquee' => {
    testimonials[]-> ${testimonial},
  },
  _type == 'callToAction' => {
    "theme": coalesce(theme, "light"),
    media[0] ${mediaWithIcon},
    headline,
    bodyCopy,
    "ctaMode": coalesce(ctaMode, "button"),
    cta[0] ${link},
    buttons[] ${cardButton},
    form -> ${form},
    formConfirmation ${formConfirmation},
    "formFunnel": coalesce(formFunnel, "contact"),
    "primaryButtonLabel": coalesce(primaryButtonLabel, "Get a demo"),
    secondaryButton[0] ${link},
    legalText[] ${basicText},
    'background': background->color.hex,
    'foreground': foreground->color.hex,
  },
  _type == 'heroBasic' => {
    overline,
    headline,
    headlineSize,
    bodyCopy,
    button ${button},
    'background': background->color.hex,
    'foreground': foreground->color.hex,
  },
  _type == 'heroProduct' => {
    productIcon ${image},
    productTitle,
    headline,
    bodyCopy,
    button ${button},
    media[0] ${media},
  },
  _type == 'homePageHero' => {
    headline,
    bodyCopy[] ${basicText},
    formSubtextLink[0] ${link},
    form -> ${form},
    formConfirmation,
    media ${image},
    mobileMedia ${image},
  },
  _type == 'awardsCta' => ${awardsCta},
  _type == 'heroTwoUp' => {
    overline,
    headline,
    bodyCopy[] ${basicText},
    button ${button},
    media[0] ${media},
    'background': background->color.hex,
    'foreground': foreground->color.hex,
  },
  _type == 'heroNewsletter' => {
    headline,
    bodyCopy,
    emailInputPlaceholder,
    submitButtonLabel,
    legalText[] ${basicText},
    trackingEventName,
    hubspotCampaignId,
    hubspotConvertingFormType,
    hubspotEmailSubscriptionIds,
    confirmation,
    media[0] ${media},
    'background': background->color.hex,
    'foreground': foreground->color.hex,
  },
  _type == 'threeUp' => ${threeUp},
  _type == 'stats' => ${stats},
  _type == 'linkCardGrid' => {
    headline,
    cards[] {
      _type,
      _type == 'linkCardReference' => @->{
        ...(card ${linkCardFields}),
        '_key': _id,
      },
      _type == 'card' => ${linkCardFields}
    }
  },
  _type == 'faqSection' => {
    questions[]-> {
      '_key': _id,
      question,
      answer[] ${basicText},
    },
  },
  _type == 'twoUpCard' => {
    headline,
    bodyCopy,
    formattedBodyCopy[] ${richText},
    cta[0] ${link},
    media[0] ${media},
    hasMediaPadding,
    'contentBackground': contentBackground->color.hex,
    'mediaBackground': mediaBackground->color.hex,
    desktopLayout,
    mobileLayout,
  },
  _type == 'photoMarquee' => {
    photos[] {
      _key,
      image ${image},
      imageWidth,
      caption[] ${basicText},
    },
  },
  _type == 'editorialCollage' => {
    overline,
    headline,
    bodyCopy,
    cta[0] ${link},
    media[] ${media},
  },
  _type == 'partnerGrid' => {
    headline,
    bodyCopy,
    gridType,
    items[] {
      _key,
      image ${image},
      title,
      caption,
    },
  },
  _type == 'newsletterListing' => {
    headline,
    'issues': *[_type == 'newsletter']| order(publishedAt desc)[0...17]  {
      '_key': _id,
      subjectLine,
      publishedAt,
      url,
      thumbnailImage ${image},
    }
  },
  _type == 'bentoBox' => {
    headline,
    'boxes': [  
      box1 ${bentoBoxItem},
      box2 ${bentoBoxItem},
      box3 ${bentoBoxItem},
      box4 ${bentoBoxItem},
      box5 ${bentoBoxItem},
      box6 ${bentoBoxItem},
    ],
  },
  _type == 'careersFeed' => {
    title,
  },
  _type == 'testimonialCarousel' => ${testimonialCarousel},
  _type == 'testimonialGrid' => {
    items[] {
      _key,
      _type,
      _type == 'testimonialCard' => {
        logoWidth,
        ...(testimonial-> ${testimonial}),
      },
      _type == 'inlineCallToAction' => {
        headline,
        link[0] ${link},
      },
    },
  },
  _type == 'logoGrid' => {
    headline,
    'background': '#F4F4F5',
    items[] {
      _key,
      logo ${image},
      logoWidth,
      link[0] ${link},
    },
  },
  _type == 'pressListing' => {
    headline,
    'items': *[_type == 'pressItem'] | order(publishedAt desc) {
      '_key': _id,
      title,
      publishedAt,
      source,
      url,
      thumbnailImage ${image},
    }
  },
  _type == 'richAccordion' => ${richAccordion},
  _type == 'richTextSection' => ${richTextSection},
  _type == 'relatedContent' => ${relatedContent},
  _type == 'homepageContentCards' => ${homepageContentCards},
  _type == 'homepageEvents' => ${homepageEvents},
  _type == 'twoUpForm' => ${twoUpForm},
  _type == 'comparisonTable' => ${comparisonTable},
  _type == 'featureComparison' => ${featureComparison},
  _type == 'comparisonCards' => ${comparisonCards},
  _type == 'documentTemplate' => {
    pageTitle,
    toc {
      excludedStrings,
      legalText[] ${basicText},
    },
    content[] ${richText},
  },
  _type == 'anchorPoint' => {
    'id': id.current,
  },
  _type == 'divider' => {
    'theme': coalesce(theme, 'white'),
  },
  _type == 'solutionsTabs' => {
    headline,
    'renderGrayscale': coalesce(renderGrayscale, false),
    tabs[] {
      _key,
      label,
      headline,
      eyebrow,
      content[] ${richText},
      link[0] ${link},
      colorTheme,
      media[0] ${media},
      mobileMedia[0] ${media},
      hasMediaPadding,
    },
  },
  _type == 'timeline' => {
    sections[] {
      _type,
      _key,
      _type == 'timelineEvent' => {
        year,
        companyHistory[] ${basicText},
        industryContext[] ${basicText},
        media[0] ${media},
        mediaSize,
        mediaPosition,
        mediaOrder,
      },
      _type == 'timelineTestimonial' => {
        ...(testimonial-> ${testimonial}),
      },
      _type == 'videoEmbed' => ${videoEmbed},
      _type == 'timelineTextBox' => {
        headline,
        content[] ${basicText},
      },
    }
  },
  _type == 'heroForm' => ${heroForm},
  _type == 'eventHero' => ${eventHero},
  _type == 'cardGrid' => ${cardGrid},
  _type == 'imageGrid' => ${imageGrid},
  _type == 'heroCentered' => ${heroCentered},
  _type == 'heroHome' => ${heroHome},
  _type == 'heroSplit' => ${heroSplit},
  _type == 'sombraStack' => ${sombraStack},
  _type == 'policyEngineAnimation' => ${policyEngineAnimation},
  _type == 'tableSection' => ${tableSection},
  _type == 'railsFormLanding' => ${railsFormLanding},
  _type == 'footnotes' => ${footnotes},
}`;
